<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>Change my product</h1>
        <div class="underline"></div>
        <div v-html="referenceData.changeMyProductSubtitle.informationText" />
      </div>
    </div>
    <div class="product-banner blue">
      <h2 class="h5">{{ selectedProducts.recommendationName }}</h2>
      <h2 class="h4 crimson">
        {{ convertToCurrencyString(selectedProducts.price) }}
        <span class="h6">/ {{ paymentFrequency }}</span>
      </h2>
      <p>
        Cover for a
        <strong>{{ summaryCoverComposition(selectedProducts.composition) }}</strong>
      </p>
      <p>
        <strong>${{ selectedExcess }}</strong>
        excess
      </p>
      <a class="secondary always-fresh" id="myRecommendations">Edit cover</a>
    </div>
    <div id="form-container">
      <div id="multi-step-form" :class="{ 'last-step': isLastFormStep() }">
        <MultiStepFormNavigation
          :currentPage="currentPage"
          :links="navLinks"
          @linkClicked="updateCurrentStep"
          :disabled="isLoading || childComponentLoading || apiStatus.referenceData.fail"
        />

        <div id="form-contents">
          <Alert
            v-if="apiStatus.referenceData.fail || apiStatus.confirmationPremium.fail || apiStatus.submitChangeCover.fail || apiStatus.fetchMemberPolicies.fail"
            :message="submitPolicyError ? ErrorStrings.SUBMIT_POLICY_API_ERROR : `Something went wrong. Please try again.`"
            alertType="error"
          >
            <template #errorMessage v-if="apiStatus.confirmationPremium.fail">
              <p>
                Sorry, we couldn’t load the information.
                <button class="non-button-style" @click="updateCurrentStep(pages.CONFIRMATION.pageNumber)">Reload</button>
                the page to continue.
              </p>
            </template>
          </Alert>

          <ApiLoading
            v-if="isLoading || apiStatus.referenceData.fail || apiStatus.confirmationPremium.fail || apiStatus.submitChangeCover.fail || apiStatus.fetchMemberPolicies.fail"
            :apiError="apiStatus.referenceData.fail || apiStatus.confirmationPremium.fail || apiStatus.submitChangeCover.fail || apiStatus.fetchMemberPolicies.fail"
            class="api-loading"
          >
            <p>Sorry, we couldn't load the information.</p>
            <p>Try refreshing your page.</p>
          </ApiLoading>

          <!-- Inactive components will be cached! -->
          <div v-else>
            <keep-alive>
              <component
                :is="getComponentName(currentPage)"
                :informationReferenceData="captureInformationReferenceData"
                :terminationReasons="terminationReasons"
                :fundNamesNewPerson="fundNamesNewPerson"
                :fundNamesTerminated="fundNamesTerminated"
                v-bind="currentProps"
                @apiLoading="updateLoadingStatus"
                @formUpdated="formResponseUpdated"
                @backButtonPressed="handleBackButton"
                @changeCoverSubmitted="submitChangeCover"
              />
            </keep-alive>
          </div>

          <div
            v-if="!isLoading"
            class="form-page-nav"
            :class="{
              'last-step': isLastFormStep(),
              'children-step': showChildrenErrorMessage,
            }"
          >
            <div class="children-step-top-level-error-message" v-if="showChildrenErrorMessage" :apiError="true">
              <svg>
                <use xlink:href="/assets/public/images/icons.svg#error" />
              </svg>
              <template v-if="isFamilyPlus">
                <!-- IF there is at least 1 non-student dependant AND another 31+ dependant-->
                <template v-if="childrenStepResponse.atLeastOneNonStudentAndAnother31Plus">
                  <p>
                    This policy can cover dependants, eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21 and
                    30 years who is not studying full-time. Whether young adults are working or still studying once they turn 31 it's time for them to take out their own cover.
                  </p>

                  <p>You can continue with this application by removing the dependant aged 31 or above.</p>
                </template>

                <!-- IF there are no non-student dependant at all -->
                <template v-else-if="childrenStepResponse.hasIneligibleDependent">
                  <p>
                    This policy can cover dependants, eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21 and
                    30 years who is not studying full-time.
                  </p>

                  <p v-if="childrenStepResponse.childrenLength > 1">
                    You cannot continue with this application. Please
                    <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update who you want cover for</a>
                    excluding dependants aged between 21 and 30 years who is not studying full-time, and we'll help you find the health cover that better meets your need.
                  </p>
                </template>
              </template>
              <template v-else-if="!isChildrenOnly">
                <!-- IF there is at least 1 child/eligible student (where there are ineligible dependants) -->
                <template v-if="childrenStepResponse.atLeastOneEligibleStudentWithAnotherIneligibleDependant">
                  <p>
                    This policy can cover dependants and eligible students who are studying full-time, single and aged between 21 and 30. You have indicated that you would like to
                    cover a dependant who is not a student, or is aged 31 or above. Whether young adults are working or still studying once they turn 31 it's time for them to take
                    out their own cover.
                  </p>

                  <p v-if="childrenStepResponse.childrenLength > 1">
                    Please review your dependant's details to ensure they are correct. You can continue with this application by removing the dependant who is not a student and/or
                    the dependant aged 31 or above. Alternatively,
                    <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update your dependant details</a>
                    and we'll help you find the health cover that better meets your need.
                  </p>
                </template>

                <!-- IF there are no eligible students at all -->
                <template v-else-if="childrenStepResponse.noStudentsNorChildrenAtAll">
                  <p>
                    This policy can cover dependants and eligible students who are studying full-time, single and aged between 21 and 30. You have indicated that none of the
                    dependants that you would like cover for are students.
                  </p>

                  <p v-if="childrenStepResponse.childrenLength > 1">
                    Please review your dependant's details to ensure they are correct. Alternatively,
                    <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update who you want cover for</a>
                    and we'll help you find the health cover that better meets your need.
                  </p>
                </template>
              </template>
            </div>
            <section class="form-buttons">
              <button id="back-button" v-if="!isFirstFormStep() && !isLastFormStep()" class="secondary" @click="handleBackButton">
                {{ referenceData.backButtonLabel.informationText }}
              </button>
              <button id="next-button" v-if="!isLastFormStep()" @click.prevent="handleNextButton" :disabled="isNextButtonDisabled()">
                {{ referenceData.nextButtonLabel.informationText }}
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>

    <div id="sub-footer">
      <p>
        Something not quite right?
        <a @click.prevent="redirectToQAPage" aria-label="Review your product">Review your product here.</a>
      </p>
    </div>
  </div>
</template>

<script>
import { each } from 'lodash';
import { Alert, ApiLoading, MultiStepFormNavigation } from '@/components';
import {
  DHLError,
  convertToCurrencyString,
  reverseFormatFormDate,
  summaryCoverComposition,
  SubmitPolicyError,
  SubmitPolicyInProcessError,
  SubmitPolicyTimeoutError,
  isPOBoxAddress,
} from '@general/js/utils';
import { CoverComposition, CoverType, CoverTypeValue, PolicyStatus } from '@general/js/enums';
import { OperationType } from '@/enums';
import { submitChangeCover } from '@/utils/ChangeProduct/SubmitChangeCoverUtils';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import JoinNowServices from '@general/js/services/JoinNow';
import getPremiumPricePayload from '@/utils/ChangeProduct/GetPremiumPrice';
import { findPolicyHolder } from '@/utils/ChangeProduct/ExtraUtils';
import { CHANGE_COVER_CONFIRMATION } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { getPartnerDetailsMP } from '@/utils/MemberUtils';
import CommonServicesMP from '@/services/Common';
import CommonServices from '@general/js/services/Common';
import { ErrorStrings } from '@general/js/constants';
import { REBATE_FORM_PDF_FILENAMES } from '@general/js/_constants';
import { convertFormToPdfDataUri } from '@/utils/FileUtils';
import { PolicyChanges, PolicyHolder, Partner, Eligibility, Rebate, Confirmation, Children } from './CaptureInformationSteps';
import CaptureInformationMixins from './CaptureInformationMixins';
import ChangeCoverMixins from './ChangeCoverMixins';

export default {
  name: 'CaptureAdditionalInformation',

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    Alert,
    ApiLoading,
    Confirmation,
    Eligibility,
    MultiStepFormNavigation,
    PolicyChanges,
    PolicyHolder,
    Rebate,
    Partner,
    Children,
  },

  data() {
    return {
      currentPage: 1,
      pages: {
        POLICY_CHANGES: {
          pageNumber: 1,
          pageTitle: 'Policy changes',
          isInvalid: true,
          componentName: 'PolicyChanges',
        },
        POLICY_HOLDER: {
          pageNumber: 2,
          pageTitle: 'Policy holder',
          isInvalid: true, // Default
          componentName: 'PolicyHolder',
        },
        PARTNER: {
          pageNumber: 3,
          pageTitle: 'Partner',
          isInvalid: true,
          componentName: 'Partner',
        },
        CHILDREN: {
          pageNumber: 4,
          pageTitle: 'Children',
          isInvalid: true,
          componentName: 'Children',
        },
        ELIGIBILITY: {
          pageNumber: 5,
          pageTitle: 'Eligibility',
          isInvalid: true,
          componentName: 'Eligibility',
        },
        REBATE: {
          pageNumber: 6,
          pageTitle: 'Rebate',
          isInvalid: true,
          componentName: 'Rebate',
        },
        CONFIRMATION: {
          pageNumber: 7,
          pageTitle: 'Confirmation',
          isInvalid: true,
          componentName: 'Confirmation',
        },
      },
      coverDetails: {
        newCover: {
          name: 'Advantage Hospital Silver Plus and Value Extras',
          excess: 750,
        },
      },
      coverStartDate: '',
      childrenStepResponse: null,
      submitPolicyError: false,
      apiStatus: {
        referenceData: {
          loading: false,
          fail: false,
        },
        confirmationPremium: {
          loading: false,
          fail: false,
        },
        submitChangeCover: {
          loading: false,
          fail: false,
        },
        fetchMemberPolicies: {
          loading: false,
          fail: false,
        },
      },
      terminationReasons: [],
      fundNames: [],
      childComponentLoading: false,
      rebateFormPdf: null,
      isInitialising: true,
      ErrorStrings,
    };
  },

  async beforeMount() {
    try {
      await this.fetchReferenceData();
      await this.getPolicyHolderInformation();

      if (this.oldCompositionHavePartner) {
        await this.getPartnerInformation();
      }

      // Initial data which could have been updated in My Cover should be carried across to the initial state of CAI
      const myCoverInitialData = {
        ...this.captureAdditionalInformationData,
        policyHolder: {
          ...this.captureAdditionalInformationData.policyHolder,
          veteranCard: this.initialData.policyHolder.veteranCard,
          portalAgeBasedDiscount: this.initialData.policyHolder.portalAgeBasedDiscount,
          lhc: this.initialData.policyHolder.lhc,
          abd: this.initialData.policyHolder.abd,
        },
        partner: {
          ...this.captureAdditionalInformationData.partner,
          veteranCard: this.initialData.partner.veteranCard,
          portalAgeBasedDiscount: this.initialData.partner.portalAgeBasedDiscount,
          lhc: this.initialData.partner.lhc,
          abd: this.initialData.partner.abd,
        },
      };

      await this.updateInitialData(myCoverInitialData);

      // Fetch active or suspended other member policies
      await this.fetchMemberPolicies(this.policyHolderStep.personCode);

      if (!this.hasPolicyChanges) {
        this.currentPage = this.pages.POLICY_HOLDER.pageNumber;
        this.pages.POLICY_CHANGES.isInvalid = false;
      }
    } finally {
      this.isInitialising = false;
    }
  },

  watch: {
    currentPage(pageNum) {
      if (
        pageNum !== this.pages.CONFIRMATION.pageNumber &&
        (this.apiStatus.confirmationPremium.fail || this.apiStatus.submitChangeCover.fail || this.apiStatus.fetchMemberPolicies.fail)
      ) {
        this.apiStatus.confirmationPremium.fail = false;
        this.apiStatus.submitChangeCover.fail = false;
        this.apiStatus.fetchMemberPolicies.fail = false;
      }
    },
  },

  computed: {
    hasPolicyChanges() {
      return this.showPolicy || this.showPartner || this.showChildren;
    },

    isFamilyPlus() {
      return [CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(this.selectedProducts.composition);
    },

    isChildrenOnly() {
      return this.selectedProducts.composition === CoverComposition.CHILDREN_ONLY;
    },

    navLinks() {
      const links = [];

      if (this.hasPolicyChanges) {
        links.push({
          linkTitle: this.pages.POLICY_CHANGES.pageTitle,
          pages: [this.pages.POLICY_CHANGES.pageNumber],
          disabled: false,
        });
      }

      links.push({
        linkTitle: this.pages.POLICY_HOLDER.pageTitle,
        pages: [this.pages.POLICY_HOLDER.pageNumber],
        disabled: this.pages.POLICY_CHANGES.isInvalid,
      });

      const disablePartnerPageLink = this.pages.POLICY_HOLDER.isInvalid;
      const disableChildrenPageLink = disablePartnerPageLink || this.pages.PARTNER.isInvalid;
      let disableEligibilityPageLink = disableChildrenPageLink || this.pages.CHILDREN.isInvalid;

      const partnerLink = { linkTitle: this.pages.PARTNER.pageTitle, pages: [this.pages.PARTNER.pageNumber], disabled: disablePartnerPageLink };
      const childrenLink = { linkTitle: this.pages.CHILDREN.pageTitle, pages: [this.pages.CHILDREN.pageNumber], disabled: disableChildrenPageLink };

      if (this.changeCoverData && this.changeCoverData.step2.composition) {
        switch (this.changeCoverData.step2.composition) {
          case CoverComposition.COUPLE:
            links.push(partnerLink);
            disableEligibilityPageLink = this.pages.POLICY_HOLDER.isInvalid || this.pages.PARTNER.isInvalid;
            break;

          case CoverComposition.SINGLE_PARENT:
          case CoverComposition.SINGLE_PARENT_PLUS:
          case CoverComposition.CHILDREN_ONLY:
            childrenLink.disabled = this.pages.POLICY_HOLDER.isInvalid;
            links.push(childrenLink);

            disableEligibilityPageLink = childrenLink.disabled || this.pages.CHILDREN.isInvalid;
            break;

          case CoverComposition.FAMILY:
          case CoverComposition.FAMILY_PLUS:
            links.push(partnerLink);
            links.push(childrenLink);
            break;

          case CoverComposition.SINGLE:
            disableEligibilityPageLink = this.pages.POLICY_HOLDER.isInvalid;
            break;

          default:
            break;
        }
      }

      const disableRebatePageLink = disableEligibilityPageLink || this.pages.ELIGIBILITY.isInvalid;
      const disableConfirmationPageLink = disableRebatePageLink || this.pages.REBATE.isInvalid;

      const staticLinks = [
        { linkTitle: this.pages.ELIGIBILITY.pageTitle, pages: [this.pages.ELIGIBILITY.pageNumber], disabled: disableEligibilityPageLink },
        { linkTitle: this.pages.REBATE.pageTitle, pages: [this.pages.REBATE.pageNumber], disabled: disableRebatePageLink },
        { linkTitle: this.pages.CONFIRMATION.pageTitle, pages: [this.pages.CONFIRMATION.pageNumber], disabled: disableConfirmationPageLink },
      ];
      return links.concat(staticLinks);
    },

    currentProps() {
      const propsData = {
        coverDetails: this.coverDetails,
        ref: this.getComponentName(this.currentPage),
      };

      return propsData;
    },

    isLoading() {
      return (
        this.isInitialising || this.apiStatus.referenceData.loading || this.apiStatus.confirmationPremium.loading || this.apiStatus.submitChangeCover.loading || this.apiStatus.fetchMemberPolicies.loading
      );
    },

    showChildrenErrorMessage() {
      if (this.getComponentName(this.currentPage) === this.pages.CHILDREN.componentName && this.childrenStepResponse !== null) {
        if (this.isFamilyPlus) {
          return this.childrenStepResponse.hasIneligibleDependent || this.childrenStepResponse.atLeastOneNonStudentAndAnother31Plus;
        }
        if (!this.isChildrenOnly) {
          return this.childrenStepResponse.atLeastOneEligibleStudentWithAnotherIneligibleDependant || this.childrenStepResponse.noStudentsNorChildrenAtAll;
        }
      }
      return false;
    },

    // Fund names property when adding a new member in the policy, either partner/child (transferring from another fund)
    // `hiccode`-`fundName` (e.g. GMH-GMHBA Health Fund) is used for the value in the dropdown because of duplicates in `hiccode`, caused problems in displaying the right name
    // Only the `hiccode` will be included in the submit change cover payload
    fundNamesNewPerson() {
      return this.fundNames.map((fund) => {
        return {
          name: fund.name,
          display: fund.name,
          value: `${fund.dhl_hiccode}-${fund.name}`,
          returnValue: `${fund.dhl_hiccode}-${fund.name}`,
          isAutomaticTransfer: fund.dhl_istransfergatewayenabled,
        };
      });
    },

    // Fund names property when terminating a member in the policy, either partner/child
    // `code` is used for the value in the dropdown, to be sent in the submit change cover payload
    fundNamesTerminated() {
      return this.fundNames.map((fund) => {
        return {
          name: fund.name,
          display: fund.name,
          value: fund.dhl_code,
          returnValue: fund.dhl_code,
          isAutomaticTransfer: fund.dhl_istransfergatewayenabled,
        };
      });
    },
  },

  methods: {
    updateLoadingStatus(emittedData) {
      this.childComponentLoading = emittedData.apiLoading;
    },

    /**
     * Get policy holder information from vuex (previously saved from change cover)
     */
    async getPolicyHolderInformation() {
      if (this.memberDetails) {
        const policyHolder = findPolicyHolder(this.memberDetails);
        if (policyHolder) {
          this.policyHolderStep.firstName = policyHolder.firstName;
          this.policyHolderStep.middleName = policyHolder.middleName;
          this.policyHolderStep.lastName = policyHolder.lastName;
          this.policyHolderStep.gender = policyHolder.sex;
          this.policyHolderStep.DOB = policyHolder.dob;
          this.policyHolderStep.mobile = policyHolder.mobile;
          this.policyHolderStep.email = policyHolder.email;
          this.policyHolderStep.personCode = policyHolder.personCode;

          const residentialAddress = policyHolder.residentialAddress;
          if (residentialAddress && !this.hasAddressStateChanged) {
            this.policyHolderStep.residentialAddress = {
              // This address type is required
              addressType: 'S',
              ...this.getAddress(policyHolder, false, false),
            };
          } else {
            this.policyHolderStep.residentialAddress = {};
          }

          this.policyHolderStep.differentPostalAddress = !!policyHolder.address.addressLine1;
          this.policyHolderStep.POBox = isPOBoxAddress(policyHolder.address.addressLine1);
          // if has  a different postal address
          if (this.policyHolderStep.differentPostalAddress) {
            this.policyHolderStep.postalAddress = this.getAddress(policyHolder, this.policyHolderStep.differentPostalAddress, this.policyHolderStep.isPOBox);
          } else {
            this.policyHolderStep.postalAddress = { ...this.policyHolderStep.residentialAddress };
          }
          this.policyHolderStep.haveConcessionCard = policyHolder.hasConcessionCard;
          if (this.policyHolderStep.haveConcessionCard) {
            this.policyHolderStep.selectedConcessionCardOptions = this.fetchConcessionCardDetails(policyHolder);
          }

          this.policyHolderStep.lhc = this.changeCoverEnteredData.step2.policyHolder.lhc;
          this.policyHolderStep.abd = this.changeCoverEnteredData.step2.policyHolder.abd;
        }
      }
    },

    async getPartnerInformation() {
      if (this.memberDetails) {
        const partner = getPartnerDetailsMP(this.memberDetails, true);

        if (partner) {
          this.partnerStep.firstName = partner.firstName;
          this.partnerStep.middleName = partner.middleName;
          this.partnerStep.lastName = partner.lastName;
          this.partnerStep.gender = partner.sex;
          this.partnerStep.DOB = partner.dob;
          this.partnerStep.mobile = partner.mobile;
          this.partnerStep.email = partner.email;
          this.partnerStep.personCode = partner.personCode;

          const residentialAddress = partner.residentialAddress;
          if (residentialAddress && !this.hasAddressStateChanged) {
            this.partnerStep.residentialAddress = this.getAddress(partner, false, false);
          } else {
            this.partnerStep.residentialAddress = {};
          }

          this.partnerStep.differentPostalAddress = !!partner.address.addressLine1;
          this.partnerStep.POBox = isPOBoxAddress(partner.address.addressLine1);
          if (this.partnerStep.differentPostalAddress) {
            this.partnerStep.postalAddress = this.getAddress(partner, this.partnerStep.differentPostalAddress, this.partnerStep.POBox);
          } else {
            this.partnerStep.postalAddress = { ...this.partnerStep.residentialAddress };
          }

          this.partnerStep.keepAdult = this.changeCoverData.step2.partner.keepAdult;

          if (this.partnerStep.keepAdult) {
            this.partnerStep.haveConcessionCard = partner.hasConcessionCard;
            if (this.partnerStep.haveConcessionCard) {
              this.partnerStep.selectedConcessionCardOptions = this.fetchConcessionCardDetails(partner);
            }
            this.partnerStep.lhc = this.changeCoverEnteredData.step2.partner.lhc;
            this.partnerStep.abd = this.changeCoverEnteredData.step2.partner.abd;
          } else {
            // Use newPartner information from My Cover
            this.partnerStep.lhc = this.changeCoverEnteredData.step2.partner.newPartner.lhc;
            this.partnerStep.abd = this.changeCoverEnteredData.step2.partner.newPartner.abd;
          }
        }
      }
    },

    getAddress(memberObj, isPostal, isPOBox) {
      // const state = this.convertCodeToValue(this.stateOptions, addressObj.dhl_state);
      if (memberObj) {
        // residential address
        if (!isPostal && !isPOBox) {
          return {
            addressLine1: memberObj.residentialAddress.addressLine1,
            addressLine2: memberObj.residentialAddress.addressLine2 ?? '',
            addressLine3: memberObj.residentialAddress.addressLine3 ?? '',
            suburb: memberObj.residentialAddress.suburb,
            postcode: memberObj.residentialAddress.postcode,
            state: memberObj.residentialAddress.state,
            dpId: memberObj.residentialAddress.dpId ?? '',
          };
        }
        if (isPostal) {
          // addressObj is actually the member object for postal address, hence the need to `addressObj.address`
          const postalAddress = {
            suburb: memberObj.address.suburb,
            postcode: memberObj.address.postcode,
            state: memberObj.address.state,
            dpId: memberObj.address.dpId ?? '',
            addressLine1: memberObj.address.addressLine1,
            addressLine2: memberObj.address.addressLine2 ?? '',
            addressLine3: memberObj.address.addressLine3 ?? '',
          };

          return postalAddress;
        }
      }
      return {};
    },

    getComponentName(currentPage) {
      // Return name of component for the page
      switch (currentPage) {
        case this.pages.POLICY_CHANGES.pageNumber:
          return this.pages.POLICY_CHANGES.componentName;
        case this.pages.POLICY_HOLDER.pageNumber:
          return this.pages.POLICY_HOLDER.componentName;
        case this.pages.PARTNER.pageNumber:
          return this.pages.PARTNER.componentName;
        case this.pages.CHILDREN.pageNumber:
          return this.pages.CHILDREN.componentName;
        case this.pages.ELIGIBILITY.pageNumber:
          return this.pages.ELIGIBILITY.componentName;
        case this.pages.REBATE.pageNumber:
          return this.pages.REBATE.componentName;
        case this.pages.CONFIRMATION.pageNumber:
          return this.pages.CONFIRMATION.componentName;
        default:
          return '';
      }
    },

    isNextButtonDisabled() {
      if (this.childComponentLoading) {
        return true;
      }
      switch (this.currentPage) {
        case this.pages.POLICY_CHANGES.pageNumber:
          return this.pages.POLICY_CHANGES.isInvalid;
        case this.pages.POLICY_HOLDER.pageNumber:
          return this.pages.POLICY_HOLDER.isInvalid;
        case this.pages.PARTNER.pageNumber:
          return this.pages.PARTNER.isInvalid;
        case this.pages.CHILDREN.pageNumber:
          return this.pages.CHILDREN.isInvalid;
        case this.pages.ELIGIBILITY.pageNumber:
          return this.pages.ELIGIBILITY.isInvalid;
        case this.pages.REBATE.pageNumber:
          return this.pages.REBATE.isInvalid;
        case this.pages.CONFIRMATION.pageNumber:
          return this.pages.CONFIRMATION.isInvalid;
        default:
          logger.error('JoinNow:: isNextButtonDisabled() - Unexpected page number passed-in');
          return true;
      }
    },

    isFirstFormStep() {
      // DE2E-13252: If policy has no changes the first page is "Policy Holder"
      return this.currentPage === (this.hasPolicyChanges ? this.pages.POLICY_CHANGES.pageNumber : this.pages.POLICY_HOLDER.pageNumber);
    },

    isLastFormStep() {
      return this.currentPage === this.pages.CONFIRMATION.pageNumber;
    },

    updateCurrentStep(newPageNumber) {
      if (newPageNumber === this.pages.CONFIRMATION.pageNumber) {
        this.captureRebateFormPdf();
        this.fetchConfirmationPremium();
      }
      this.currentPage = newPageNumber;
    },

    handleNextButton() {
      if (this.changeCoverData && this.changeCoverData.step2.composition) {
        const composition = this.changeCoverData.step2.composition;
        // Calculate where the page should go to
        let nextPage;
        switch (this.currentPage) {
          case this.pages.POLICY_HOLDER.pageNumber:
            switch (composition) {
              case CoverComposition.SINGLE_PARENT:
              case CoverComposition.CHILDREN_ONLY:
              case CoverComposition.SINGLE_PARENT_PLUS:
                nextPage = this.pages.CHILDREN.pageNumber;
                break;

              case CoverComposition.SINGLE:
                nextPage = this.pages.ELIGIBILITY.pageNumber;
                break;

              default:
                break;
            }
            break;

          case this.pages.PARTNER.pageNumber:
            if (composition === CoverComposition.COUPLE) {
              nextPage = this.pages.ELIGIBILITY.pageNumber;
            }
            break;

          default:
            nextPage = this.currentPage + 1;
            break;
        }
        if (nextPage) {
          this.updateCurrentStep(nextPage);
        } else {
          this.updateCurrentStep(this.currentPage + 1);
        }
        window.scrollTo(0, 0); // scroll to top
      }
    },

    handleBackButton() {
      let previousPage;
      if (this.changeCoverData && this.changeCoverData.step2.composition) {
        const composition = this.changeCoverData.step2.composition;
        if (this.currentPage === this.pages.ELIGIBILITY.pageNumber) {
          if (composition === CoverComposition.SINGLE) {
            previousPage = this.pages.POLICY_HOLDER.pageNumber;
          } else if (composition === CoverComposition.COUPLE) {
            previousPage = this.pages.PARTNER.pageNumber;
          }
        } else if (this.currentPage === this.pages.CHILDREN.pageNumber) {
          if (composition === CoverComposition.SINGLE_PARENT || composition === CoverComposition.SINGLE_PARENT_PLUS || composition === CoverComposition.CHILDREN_ONLY) {
            previousPage = this.pages.POLICY_HOLDER.pageNumber;
          }
        }
        if (previousPage) {
          this.updateCurrentStep(previousPage);
        } else {
          this.updateCurrentStep(this.currentPage - 1);
        }

        window.scrollTo(0, 0); // scroll to top
      }
    },

    formResponseUpdated(emittedData) {
      switch (this.currentPage) {
        case this.pages.POLICY_CHANGES.pageNumber:
          this.pages.POLICY_CHANGES.isInvalid = emittedData.isInvalid;
          this.updatePolicyChangesStep(emittedData.response);
          break;
        case this.pages.POLICY_HOLDER.pageNumber:
          this.pages.POLICY_HOLDER.isInvalid = emittedData.isInvalid;
          this.updatePolicyHolderStep(emittedData.response);
          break;
        case this.pages.PARTNER.pageNumber:
          this.pages.PARTNER.isInvalid = emittedData.isInvalid;
          this.updatePartnerStep(emittedData.response);
          break;
        case this.pages.CHILDREN.pageNumber:
          this.pages.CHILDREN.isInvalid = emittedData.isInvalid;
          this.childrenStepResponse = emittedData.stepResponse;
          this.updateChildrenStep(emittedData.response);
          break;
        case this.pages.ELIGIBILITY.pageNumber:
          this.pages.ELIGIBILITY.isInvalid = emittedData.isInvalid;
          this.updateEligibilityStep(emittedData.response);
          break;
        case this.pages.REBATE.pageNumber:
          this.pages.REBATE.isInvalid = emittedData.isInvalid;
          this.updateRebateStep(emittedData.response);
          break;
        case this.pages.CONFIRMATION.pageNumber:
          break;
        default:
          break;
      }
    },

    /**
     * Fetches reference data (only mock data is used for CRM)
     */
    async fetchReferenceData() {
      this.apiStatus.referenceData.loading = true;
      this.apiStatus.referenceData.fail = false;
      try {
        const referenceData = await Promise.all([JoinNowServices.getReferenceData(), Services.getTerminationReasonFlexCodes(), Services.getFundNames()]);
        if (referenceData) {
          this.updateCaptureInformationReferenceData(referenceData[0]);

          const terminationReasons = referenceData[1];
          // Transform object to add 'name' & 'value' keys for dropdowns
          this.terminationReasons = each(terminationReasons, (reasons) => {
            reasons.forEach((r) => {
              r.name = r.description;
              r.value = r.code;
            });
          });

          this.fundNames = referenceData[2];
        } else {
          throw new DHLError(`Issue with the response: getCaptureAdditionalInformationReferenceData`);
        }
      } catch (error) {
        this.apiStatus.referenceData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.referenceData.loading = false;
      }
    },

    /**
     *
     */
    fetchConcessionCardDetails(memberObj) {
      try {
        const concessionDetails = memberObj?.activeConcessionCard;

        if (concessionDetails) {
          const concessionCards = [
            {
              expire: concessionDetails.expiryDate ? reverseFormatFormDate(concessionDetails.expiryDate) : '',
              name: concessionDetails.cardType,
              id: concessionDetails.id,
              number: concessionDetails.cardNumber,
              startDate: concessionDetails.startDate ? reverseFormatFormDate(concessionDetails.startDate) : '',
              endDate: concessionDetails.endDate ? reverseFormatFormDate(concessionDetails.endDate) : '',
              op: OperationType.NONE,
            },
          ];

          return concessionCards;
        }
        return [];
      } catch (error) {
        return null;
      }
    },

    async fetchConfirmationPremium() {
      this.apiStatus.confirmationPremium.loading = true;
      this.apiStatus.confirmationPremium.fail = false;

      try {
        const payload = this.getPremiumPayload();
        const newPremium = await Services.getPrice(payload);
        if (newPremium.productPrices && newPremium.productPrices.length > 0) {
          this.selectedProducts.price = newPremium.productPrices[0].calculationResultSetTotalResult;

          if (newPremium.productPrices[0].discounts && Object.keys(newPremium.productPrices[0].discounts).length > 0) {
            this.selectedProducts.discountsAndLoadings = { ...newPremium.productPrices[0].discounts };
          }
        }
      } catch (error) {
        logger.error('The error is: ', error);
        this.apiStatus.confirmationPremium.fail = true;
      } finally {
        this.apiStatus.confirmationPremium.loading = false;
      }
    },

    async submitChangeCover() {
      this.apiStatus.submitChangeCover.loading = true;
      this.apiStatus.submitChangeCover.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);

      try {
        const payload = this.formatChangeCoverSubmitPayload();
        const response = await Services.submitChangeCover(this.memberNumber, payload);

        if (this.rebateFormPdf) {
          await CommonServices.uploadRebateFormPdf(this.memberNumber, REBATE_FORM_PDF_FILENAMES.MEMBER_PORTAL_CHANGE_PRODUCT_ADD_REBATE, this.rebateFormPdf);
        }

        if (response.newPayFrequency) {
          // routeParams is for change cover confirmation page
          const routeParams = {
            memberNumber: this.memberNumber,
            productInfo: {
              productName: this.selectedProducts.recommendationName,
              composition: this.selectedProducts.composition,
              productType: this.selectedProducts.coverType,
              rebateOp: this.rebateInformationStep.op,
              productExcessType: this.getProductExcessType(this.selectedProducts.products),
              excess: this.selectedExcess,
              oldPremium: response.currentPremium,
              newPremium: response.newPremium ?? this.selectedProducts.price, // get the premium from confirmation step call if newPremium is not returned
              frequency: response.newPayFrequency, // frequency is not changed
              effectiveDate: this.changeCoverData.step1.startDate,
            },
          };
          setNavigationState(routeParams);
          this.$router.push({ name: CHANGE_COVER_CONFIRMATION });
        }
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = this.formatChangeCoverSubmitPayload();
            const request = {
              functionName: 'change-cover',
              workItemCategory: 'OTHERS',
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };
            await CommonServicesMP.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page
          setNavigationState({ flowName: 'change cover' });
          this.$router.push({ name: CHANGE_COVER_CONFIRMATION });
        } else {
          this.apiStatus.submitChangeCover.fail = true;
        }
      } finally {
        this.apiStatus.submitChangeCover.loading = false;
      }
    },

    getPremiumPayload() {
      const currentPremium = this.membershipPolicy.premiumInformation.currentOngoingPremium ? Number(this.membershipPolicy.premiumInformation.currentOngoingPremium) : '';
      this.updateCurrentPremium(currentPremium);
      this.updateCurrentCoverStartDate(this.membershipPolicy.coverStartDate);

      // convert 'extras' to 'extras only' for use in the mapPerson() function
      const convertedCoverType = this.changeCoverData.step2.coverType === CoverTypeValue.EXTRAS_ONLY ? CoverType.EXTRAS_ONLY : this.changeCoverData.step2.coverType;
      const caiData = {
        memberNumber: this.memberNumber,
        policyHolderStep: this.policyHolderStep,
        partnerStep: this.partnerStep,
        showNewPartnerSection: this.showNewPartnerSection,
        newCompositionHavePartner: this.newCompositionHavePartner,
        childrenStep: this.childrenStep,
        newCompositionHaveChildren: this.isCompositionHaveChildren(this.changeCoverData.step2.composition),
        oldCompositionHaveChildren: this.isCompositionHaveChildren(this.currentComposition),
        eligibilityInformationStep: this.eligibilityInformationStep,
        rebateInformationStep: this.rebateInformationStep,
      };

      const quoteId = this.myRecommendations.recommendations ? this.myRecommendations.recommendations[0].quoteId : null;

      const hospitalSituations = { policyHolder: this.policyHolderState, partner: this.partnerHospitalState };

      const payload = getPremiumPricePayload(
        this.membershipPolicy,
        this.memberDetails,
        this.captureInformationReferenceData,
        this.changeCoverData,
        caiData,
        convertedCoverType,
        this.changeCoverEnteredData,
        quoteId,
        hospitalSituations,
      );

      return payload;
    },

    formatChangeCoverSubmitPayload() {
      const extraData = {
        memberNumber: this.memberNumber,
        currentComposition: this.currentComposition,
        currentExcess: this.currentExcess,
        showPolicy: this.showPolicy, // Boolean on whether the product codes have changed
        showNewPartnerSection: this.showNewPartnerSection, // Boolean to check if a new partner was added
        oldCompositionHavePartner: this.oldCompositionHavePartner, // Boolean to check if old composition had partner
        newCompositionHavePartner: this.newCompositionHavePartner, // Boolean to check if new composition has partner
        oldCompositionHaveChildren: this.oldCompositionHaveChildren, // Boolean to check if old composition had children
        newCompositionHaveChildren: this.newCompositionHaveChildren, // Boolean to check if new composition has children
        transferToFundValues: this.transferToFundValues, // List of enum values of termination reasons that require fund names
        initialRebateTier: this.currentRebateTier, // rebate Tier value retrieved from CRM
        initialEligibilityData: this.getEligibilityInitialData,
        fundNamesNewPerson: this.fundNamesNewPerson,
        memberPolicies: this.memberPolicies,
      };

      return submitChangeCover(this.changeCoverEnteredData, this.captureAdditionalInformationData, this.initialData, this.memberDetails, extraData);
    },

    async captureRebateFormPdf() {
      if (this.$refs.Rebate.isFormValid && this.$refs.Rebate.isAddRebate && this.$refs.Rebate.formResponse.isRebateApplied && this.$refs.Rebate.formResponse.isMedicareEligible) {
        this.rebateFormPdf = await convertFormToPdfDataUri(this.$refs.Rebate.$refs.rebateForm);
      }
    },

    /**
     * Fetches all the policies on the accessing person.
     * The requesting person needs to be on the requesting policy.
     * @param {string} accessingPersonCode
     * @returns {object} array of memberships
     */
    async fetchMemberPolicies(accessingPersonCode) {
      this.apiStatus.fetchMemberPolicies.loading = true;
      this.apiStatus.fetchMemberPolicies.fail = false;
      try {
        const memberPolicies = await Services.getMemberPolicies(this.memberNumber, accessingPersonCode);

        // Filter active and suspended policies only
        const filteredPolicies = memberPolicies.memberships.filter((membership) => {
          return membership.policyStatus === PolicyStatus.ACTIVE || membership.policyStatus === PolicyStatus.SUSPENDED;
        });

        this.updateMemberPolicies(filteredPolicies);
      } catch (error) {
        logger.error(error);
        this.apiStatus.fetchMemberPolicies.fail = true;
      } finally {
        this.apiStatus.fetchMemberPolicies.loading = false;
      }
    },

    convertToCurrencyString,
    reverseFormatFormDate,
    summaryCoverComposition,
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  max-width: 1216px;
  margin: 0 auto;
  margin-top: 3.125rem;

  .form-buttons {
    margin-bottom: $CHAT_BUTTON_TOP_MARGIN;
  }

  header {
    position: relative;
    width: 100vw;
    left: 50%;
    right: 50%;
    margin: rem(-50) -50vw rem(50) -50vw;
    background: $LIGHT_BLUE_TINT;
    padding: rem(35) 0;
    overflow: hidden;

    @include mobile {
      width: 100vw;
      padding: 0;
      margin-bottom: 0;
    }

    .white-ribbon {
      min-height: rem(167);
      background: url('/assets/public/images/ribbon-2-white.svg') no-repeat bottom;
      background-size: rem(180) auto;
      background-position-x: calc(50vw - 540px);
      display: flex;

      @include tablet {
        background-position-x: 4vw;
      }

      @include mobile {
        min-height: auto;
        background-position-x: rem(-38);
        background-position-y: rem(-81);
      }

      &::before {
        content: '';
        position: absolute;
        background-color: $WHITE;
        height: rem(54);
        width: calc(50vw - 540px);
        bottom: rem(110);

        @include tablet {
          width: 4vw;
        }

        @include mobile {
          background: none;
        }
      }

      &::after {
        content: '';
        position: absolute;
        background-color: $WHITE;
        height: rem(53);
        width: calc(100vw - 50vw + 540px - 179px);
        left: calc(179px + 50vw - 540px);
        bottom: 0;

        @include tablet {
          width: calc(96vw - 179px);
          left: calc(179px + 4vw);
        }

        @include mobile {
          width: calc(100vw + 38px - 179px);
          left: calc(179px - 38px);
          bottom: auto;
          top: rem(29);
        }
      }
    }

    .header-content {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      grid-template-rows: auto;
      max-width: rem(1100);
      margin: 0 auto;
      padding-top: rem(10);
      grid-column-gap: 0;

      @include tablet {
        width: calc(100% - (20px * 2));
        padding-left: rem(20);
        padding-right: rem(20);
      }

      @include mobile {
        grid-template-columns: auto;
        grid-template-rows: auto auto;
        grid-column-gap: 0;
        padding: rem(25);

        p {
          margin-bottom: rem(10);
        }
      }
    }

    h1 {
      font-size: font-size(4);
      font-weight: $BOLD;
      min-height: rem(100);

      @include tablet {
        font-size: font-size(3);
      }

      @include mobile {
        font-size: font-size(2);
        min-height: rem(60);
      }
    }

    .cover-info {
      grid-column: 2/3;
      justify-content: flex-end;
      display: flex;
      flex-direction: column;
      z-index: 1;
      row-gap: 0.8em;

      h2 {
        margin: 0;
      }

      @include desktop {
        margin-left: calc(30px * 1);
      }

      @include tablet {
        margin-left: calc(30px * 0.7);
      }

      .start-date {
        display: none;
      }

      @include mobile {
        grid-column: 1/2;
        margin-left: 0;

        .start-date {
          display: inline-block;
        }
      }
    }

    .cover-details {
      grid-column: 3/4;
      justify-content: flex-end;
      display: flex;
      flex-direction: column;
      z-index: 1;

      @include tablet-and-above {
        margin-left: rem(40);

        p:not(:last-of-type) {
          margin-bottom: rem(30);
        }
      }

      // When Hospital excess goes to two lines
      @media screen and (min-width: $TABLET_MIN) and (max-width: 887px) {
        p:last-of-type {
          margin-bottom: rem(7);
        }
      }

      @include mobile {
        grid-column: 1/2;

        p:last-of-type {
          margin-bottom: 0;
        }

        .date-info {
          display: none;
        }
      }
    }
  }
}

.form-page-nav {
  margin-top: 5px;

  &.last-step {
    margin-top: 0;
  }

  &.children-step {
    padding: rem(33px) 0;

    &:before {
      content: '';
      display: block;
      height: 100%;
      width: 150vw;
      background-color: $GREY_300;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }

    .children-step-top-level-error-message {
      display: flex;
      flex-direction: column;

      svg {
        width: 28px;
        height: 28px;
        color: $GREY_800;
        margin: 0 auto 30px;
      }
    }
  }

  section {
    grid-row: revert !important;
  }
}

.product-banner {
  padding: 2rem 0;

  .crimson {
    color: $PRIMARY_GREEN_100;
  }

  p {
    font-size: $CAPTION_FONT_SIZE;
  }

  h2,
  p {
    margin-bottom: 4px;
  }
}
</style>
